import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@vidahealth/vida-ui';
import { type ReactNode } from 'react';
import { Platform, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { FrostedSurface } from '#modules/ui/FrostedSurface';

const DEFAULT_HEIGHT_RATIO = 0.85;

type BottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Body content. Sits in the scrollable area with horizontal + vertical padding. */
  children: ReactNode;
  /**
   * Optional pinned-to-top header rendered inside a FrostedSurface so content
   * scrolling underneath shows through with a frost effect. Use this for the
   * sheet's title/greeting when you want it to stay visible during scroll.
   */
  stickyHeader?: ReactNode;
  /**
   * Optional footer (typically a primary Button) below the scrollview. Gets
   * safe-area bottom padding automatically — consumers just pass the control.
   */
  footer?: ReactNode;
  /**
   * Maximum fraction of viewport height the sheet may occupy. Defaults to
   * 0.85. The sheet sizes to its content; this only caps the upper bound
   * when content would otherwise exceed it (then the body scrolls).
   */
  heightRatio?: number;
};

/**
 * Shared bottom sheet wrapper used by the onboarding flow. Encapsulates the
 * Actionsheet/Backdrop/Content/DragIndicator chrome, the 600-px-max body
 * column, and the safe-area-aware footer area, so each individual sheet only
 * has to describe its own header, body, and CTA.
 */
export function BottomSheet({
  isOpen,
  onClose,
  children,
  stickyHeader,
  footer,
  heightRatio = DEFAULT_HEIGHT_RATIO,
}: BottomSheetProps) {
  const { height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const sheetHeight = windowHeight * heightRatio;

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop animate={{ opacity: 0.8 }} />
      <ActionsheetContent
        // vida-ui's ActionsheetContent ships its `p-5 pt-2 … pb-safe` via its
        // base className, so `p-0` here clears it (tailwind-merge keeps the last
        // padding utility). This wrapper owns body/footer padding itself, which
        // avoids double bottom-safe spacing under the pinned CTA.
        className={'overflow-hidden p-0'}
        style={{ maxHeight: sheetHeight }}
      >
        <ActionsheetScrollView
          className='w-full'
          stickyHeaderIndices={stickyHeader ? [0] : undefined}
        >
          {stickyHeader ? (
            // Edge-to-edge frost so the sticky bar reads as a unified chrome
            // strip; the inner wrapper applies the same max-w-600 + px-6 as
            // the body underneath so the title aligns with body content.
            // `relative` gives FrostedSurface's absolute blur-fill layer a
            // containing block (the header isn't otherwise positioned).
            <FrostedSurface className='relative w-full'>
              <View className='mx-auto w-full max-w-[600px] px-6 pt-6'>{stickyHeader}</View>
            </FrostedSurface>
          ) : null}

          <View className={'mx-auto w-full max-w-[600px] px-6 py-6'}>{children}</View>

          {/* No pinned footer → restore the bottom safe-area inset that vida-ui's
              cleared `pb-safe` used to provide, so sheets whose action sits in
              the scroll body (or have none) don't tuck content under the home
              indicator. The footer branch below owns this inset when present. */}
          {!footer && Platform.OS !== 'web' && insets.bottom > 0 ? (
            <View testID='bottom-sheet-safe-area-spacer' style={{ height: insets.bottom }} />
          ) : null}
        </ActionsheetScrollView>

        {footer ? (
          <View
            className='mx-auto w-full max-w-[600px] px-6'
            style={{
              paddingTop: 12,
              // Add the native bottom safe-area inset so the CTA clears the
              // home indicator / nav bar; web has no inset, keep a flat 16.
              paddingBottom: Platform.OS === 'web' ? 16 : 8 + insets.bottom,
            }}
          >
            {footer}
          </View>
        ) : null}

        <ActionsheetDragIndicatorWrapper
          className={'absolute left-0 right-0 top-2 z-10 items-center'}
        >
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>
      </ActionsheetContent>
    </Actionsheet>
  );
}
